import { CommonModule } from '@angular/common';
import { Component, computed, Input, input, InputSignal, OnChanges, output, OutputEmitterRef, Signal, signal, SimpleChanges, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateTime, Interval } from 'luxon';
import { NgxMaskDirective } from 'ngx-mask';
import { DescricaoCalendario } from 'src/app/domain/enums/descricao-calendario.enum';
import { ETipoCalendario } from 'src/app/domain/enums/tipo-calendario.enum';
import { Cronogramas } from 'src/app/domain/modelsForComponents/cronograma.model';

@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    NgxMaskDirective,
  ],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.css'
})
export class CalendarComponent implements OnChanges {
  @Input() LimiteMinData: DateTime = DateTime.fromJSDate(new Date('1892-04-21'));
  @Input() LimiteMaxData: DateTime = DateTime.local();
  @Input() ObterDataUltimaEdicao = true;
  @Input() ExibirLegendaPadrao = true;
  @Input() TipoCalendario: ETipoCalendario = ETipoCalendario.padrao;
  @Input() customIconStyle = '';
  @Input() textoNavegaDiaAtual = "Hoje";

  dt?: Date;
  Cronogramas: InputSignal<Cronogramas> = input.required();
  dataInicial: InputSignal<DateTime | undefined> = input();
  dataClicada: OutputEmitterRef<DateTime> = output();
  alternarMes: OutputEmitterRef<DateTime> = output();
  redirecionarUltimaEdicao = output<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["dataInicial"]?.previousValue != changes["dataInicial"]?.currentValue) {
      this.diaAtivo.set(this.dataInicial() ?? DateTime.local());
      this.primeiroDiaMesAtivo.set(this.dataInicial()?.startOf('month') ?? DateTime.local().startOf('month'));
      this.dt = this.diaAtivo()?.toJSDate();
    }
    const i: DescricaoCalendario[] = []
    i.findIndex(x => x === 1)
  }

  hoje: Signal<DateTime> = signal(DateTime.local());
  primeiroDiaMesAtivo: WritableSignal<DateTime> = signal(
    this.dataInicial()?.startOf('month') ?? this.hoje().startOf('month'),
  );

  diaAtivo: WritableSignal<DateTime | null> = signal(null);
  diasSemana: Signal<string[]> = signal(['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab']);
  datasDoMes: Signal<DateTime[]> = computed(() => {
    return Interval.fromDateTimes(
      this.primeiroDiaMesAtivo().startOf('week', { useLocaleWeeks: true, }),
      this.primeiroDiaMesAtivo().endOf('month', { useLocaleWeeks: true }).endOf('week', { useLocaleWeeks: true }),
    )
      .splitBy({ day: 1 })
      .map((d: Interval) => {
        if (d.start === null) {
          throw new Error('Datas incorretas');
        }
        return d.start;
      });
  });

  DATE_MED = DateTime.DATE_MED;

  diaAtivoCronogramas: Signal<DescricaoCalendario[]> = computed(() => {
    const diaAtivo = this.diaAtivo();
    if (diaAtivo === null) {
      return [];
    }
    const diaAtivoISO = diaAtivo.toISODate();

    if (!diaAtivoISO) {
      return [];
    }

    return this.Cronogramas()[diaAtivoISO] ?? [];
  });

  goToMesAnterior(): void {
    this.primeiroDiaMesAtivo.set(
      this.primeiroDiaMesAtivo().minus({ month: 1 }).plus({ hours: 3 }),
    );
    this.alternarMes.emit(this.primeiroDiaMesAtivo());
  }

  goToMesSeguinte(): void {
    this.primeiroDiaMesAtivo.set(
      this.primeiroDiaMesAtivo().plus({ month: 1, hours: 3 }),
    );
    this.alternarMes.emit(this.primeiroDiaMesAtivo());
  }

  goToHoje(): void {
    if (this.ObterDataUltimaEdicao)
      this.redirecionarUltimaEdicao.emit();
    else {
      this.primeiroDiaMesAtivo.set(this.hoje().startOf('month'));
      this.alternarMes.emit(this.hoje());
    }
  }

  onDiaClicado(dia: DateTime): void {
    const dataTratada = this.tratarDataHoras(dia);
    this.diaAtivo.set(dataTratada);
    this.dataClicada.emit(dataTratada);
  }

  tratarDataHoras(d: DateTime) {
    if (d && d < DateTime.local(1914, 1, 2, { zone: "utc" }))
      d = d?.plus({ hours: 3, minutes: 7 });
    else
      d = d?.plus({ hours: 3 });
    return d;
  }

  trocarMesPorInputDatePicker($event: Date) {
    const data = new Date($event);
    if (data && !isNaN(data.getDate()) &&
      data >= this.LimiteMinData.toJSDate() && data <= this.LimiteMaxData.toJSDate()
    ) {
      this.dt = new Date($event);
      if (data.getMonth() + 1 !== this.diaAtivo()?.month || data.getFullYear() !== this.diaAtivo()?.year) {
        this.alternarMes.emit(DateTime.utc(data.getUTCFullYear(), data.getUTCMonth() + 1, data.getUTCDate(), 3));
      }
      this.diaAtivo.set(DateTime.fromJSDate(data, { zone: "utc" }));
      this.primeiroDiaMesAtivo.set(DateTime.fromJSDate(data, { zone: "utc" })?.startOf('month', { useLocaleWeeks: true }));
    }
  }

  trocarMesPorInputPadrao($event: Date) {
    const data = new Date($event);
    if (data && !isNaN(data.getDate()) &&
      data >= this.LimiteMinData.toJSDate() && data <= this.LimiteMaxData.toJSDate()
    ) {
      this.diaAtivo.set(DateTime.fromJSDate(data, { zone: "utc" }));
      this.primeiroDiaMesAtivo.set(DateTime.fromJSDate(data, { zone: "utc" })?.startOf('month', { useLocaleWeeks: true }));
      this.alternarMes.emit(DateTime.utc(data.getUTCFullYear(), data.getUTCMonth() + 1, data.getUTCDate(), 3));
      this.dataClicada.emit(this.tratarDataHoras(DateTime.fromJSDate(data, { zone: "utc" })));
    }
  }

  isTypeCalendarPadrao() {
    return this.TipoCalendario === ETipoCalendario.padrao;
  }

  getCalendarCssClass() {
    return this.TipoCalendario === ETipoCalendario.padrao ? ['calendar'] : [];
  }

  getHeadlineMonth() {
    const classes: string[] = [];
    if (this.TipoCalendario === ETipoCalendario.exibicaoPublicacoes)
      classes.push('calendar-headline-tela-publicacoes');
    else
      classes.push('calendar-headline');
    return classes;
  }

  getHeadlineControlsColor() {
    return this.TipoCalendario === ETipoCalendario.exibicaoPublicacoes ? 'white' : 'black';
  }

  getCalendarContainer() {
    const classes: string[] = [];
    if (this.TipoCalendario === ETipoCalendario.exibicaoPublicacoes)
      classes.push('calendar-container-tela-publicacao');
    else
      classes.push('calendar-container');
    return classes;
  }

  getHeadlineWeeks() {
    const classes: string[] = [];
    if (this.TipoCalendario === ETipoCalendario.exibicaoPublicacoes)
      classes.push('calendar-weeks-grid-tela-publicacao')
    else
      classes.push('calendar-weeks-grid')
    return classes;
  }

  getMesAnoCor() {
    const classes: string[] = [];
    if (this.TipoCalendario === ETipoCalendario.exibicaoPublicacoes)
      classes.push('descricao-mes-ano-header-tela-publicacao');
    return classes;
  }

  getGridCalendario() {
    const classes: string[] = [];
    if (this.TipoCalendario === ETipoCalendario.padrao)
      classes.push('calendar-grid')
    else if (this.TipoCalendario === ETipoCalendario.exibicaoPublicacoes)
      classes.push('calendar-grid-tela-publicacoes')
    return classes;
  }

  getCalendarCellClasses(data: DateTime): string[] {
    const classes: string[] = [];
    if (this.TipoCalendario === ETipoCalendario.padrao) {
      classes.push('calendar-grid-cell');
      if (this.diaAtivo()?.toISODate() === data.toISODate())
        classes.push('calendar-grid-cell-active');
      if (data.month !== this.primeiroDiaMesAtivo().month)
        classes.push('calendar-grid-cell-outro-mes');
      else if ((data.weekday === 7 || data.weekday === 1) && data.month === this.primeiroDiaMesAtivo().month)
        classes.push('calendar-grid-cell-sem-expediente');

    }
    else if (this.TipoCalendario === ETipoCalendario.exibicaoPublicacoes) {
      classes.push('calendar-grid-cell-tela-publicacoes');
      if (data.month !== this.primeiroDiaMesAtivo().month)
        classes.push('calendar-grid-cell-tela-publicacoes-outro-mes')
      if ((data.weekday === 7 || data.weekday === 1))
        classes.push(data.month !== this.primeiroDiaMesAtivo().month ? 'calendar-grid-cell-tela-publicacoes-sem-publicacao-outro-mes' : 'calendar-grid-cell-tela-publicacoes-sem-publicacao-dia-especifico');
      else if (this.Cronogramas() && this.Cronogramas()[data.toISODate() as string]?.find((x: DescricaoCalendario) => x === DescricaoCalendario.SemPublicacao)) {
        classes.push(data.month !== this.primeiroDiaMesAtivo().month ? 'calendar-grid-cell-tela-publicacoes-sem-publicacao-outro-mes' : 'calendar-grid-cell-tela-publicacoes-sem-publicacao');
      } else
        classes.push(data.month !== this.primeiroDiaMesAtivo().month ? 'calendar-grid-cell-tela-publicacoes-tem-publicacao-outro-mes' : 'calendar-grid-cell-tela-publicacoes-tem-publicacao');
    }
    return classes;
  }

  simularCliqueInputDatePicker() {
    const e = document.getElementById('dataCalendario') as HTMLInputElement;
    e.showPicker();
  }

  trocarMesPorInputMobile($event?: string) {
    if (!$event)
      $event = (document.getElementById("dataCalendarioPadraoMobile") as HTMLInputElement).value;
    if ($event.length !== 10)
      return;

    const arrayData = this.extrairDadosDataString($event);
    if (arrayData.length === 3 && DateTime.fromObject({
      year: arrayData[2],
      month: arrayData[1],
      day: arrayData[0]
    }).isValid)
      this.trocarMesPorInputPadrao(new Date(arrayData[2], arrayData[1] - 1, arrayData[0]))
  }

  sincronizarDatePickerComInputMobile() {
    const dia = this.dt && this.dt?.getUTCDate() > 9 ? this.dt.getUTCDate() : `0${this.dt?.getUTCDate()}`;
    const mes = this.dt && this.dt?.getUTCMonth() > 9 ? this.dt?.getUTCMonth() + 1 : `0${(this.dt?.getUTCMonth() ?? 0) + 1}`
    const ano = this.dt?.getUTCFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  private extrairDadosDataString(dateString: string) {
    return dateString.includes('/') && dateString.length === 10 ? dateString.split('/').map(x => parseInt(x)) : [];
  }
}
